import { Color, Spacing } from '@persona-id/design-tokens';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ConditionalRow from 'dashboard/components/GraphQueryEditor/ConditionalRow';
import { ClusterQueryData, NHopsQueryData } from 'dashboard/components/GraphQueryEditor/form-state';
import MinClusterSize from 'dashboard/components/GraphQueryEditor/MinClusterSize';
import NumHops from 'dashboard/components/GraphQueryEditor/NumHops';
import {
  ByClusterCreatedAtConditional,
  Conditional,
  LeftConstants,
  Operator,
  QueryConditionalConfig,
  QueryConfig,
  QueryType,
} from 'dashboard/models/graph-query';

const Well = styled.div`
  display: grid;
  grid-template-columns: 3.5% 1.5% 10% 5% 18% auto 2.5%;
  grid-gap: ${Spacing[2]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const Separator = styled.div`
  height: 1px;
  background-color: #d7d8df;
  grid-column: 1 / 8;
`;

interface ByClusterQueryBodyProps {
  clusterQueryData: ClusterQueryData;
  loadingGraphEnvironment: boolean;
  queryConfig: QueryConfig;
  setClusterQueryData: (clusterQueryData: ClusterQueryData) => void;
  setNHopsQueryData: (nHopsQueryData: NHopsQueryData) => void;
  sharedPropertiesConfig: QueryConditionalConfig;
  showErrors: boolean;
}

const ByClusterQueryBody = ({
  clusterQueryData,
  loadingGraphEnvironment,
  queryConfig,
  setClusterQueryData,
  setNHopsQueryData,
  sharedPropertiesConfig,
  showErrors,
}: ByClusterQueryBodyProps) => {
  const createdAtConditional = clusterQueryData.createdAtConditional;
  const setCreatedAtConditional = (createdAtConditional: ByClusterCreatedAtConditional) => {
    setClusterQueryData({
      ...clusterQueryData,
      createdAtConditional,
    });
  };
  const sharedPropertiesConditional = clusterQueryData.sharedPropertiesConditional;
  const setSharedPropertiesConditional = (sharedPropertiesConditional: Conditional) => {
    setClusterQueryData({
      ...clusterQueryData,
      sharedPropertiesConditional,
    });
  };
  const minClusterSize = clusterQueryData.minimumClusterSize ?? 2;
  const setMinClusterSize = (minimumClusterSize: number) => {
    setClusterQueryData({
      ...clusterQueryData,
      minimumClusterSize,
    });
  };
  const numHops = clusterQueryData.numberOfHops ?? 2;
  const setNumHops = (numberOfHops: number) => {
    setClusterQueryData({
      ...clusterQueryData,
      numberOfHops,
    });
  };

  const selectedLeftValues = new Set<string>([createdAtConditional.left]);
  const possibleLeftConfigs = queryConfig.filter(
    (qc) => createdAtConditional.left === qc.left.key || !selectedLeftValues.has(qc.left.key),
  );

  const sharedPropertiesConfigWithAllOption = sharedPropertiesConfig
    ? {
        ...sharedPropertiesConfig,
        operatorToRight: {
          ...sharedPropertiesConfig.operatorToRight,
          [Operator.IncludesAnyOf]: {
            ...sharedPropertiesConfig.operatorToRight[Operator.IncludesAnyOf],
            allOption: { label: 'Any Property', value: '*' },
          },
        },
      }
    : undefined;

  const handleChangeConditional = (newConditional: Conditional) => {
    if (newConditional.left === LeftConstants.ByClusterCreatedAt) {
      setCreatedAtConditional(newConditional as ByClusterCreatedAtConditional);
    } else {
      // The user switched from a by-clusters query to an n-hops query.
      setNHopsQueryData({
        queryType: QueryType.NHopsFromAccount,
        startingSetConditionals: [newConditional],
        sharedPropertiesConditional: clusterQueryData.sharedPropertiesConditional,
        traversalConditionals: [],
        variables: [],
        numberOfHops: clusterQueryData.numberOfHops,
      });
    }
  };

  return (
    <>
      {!loadingGraphEnvironment && (
        <Well>
          <ConditionalRow
            key={createdAtConditional.left}
            conditional={createdAtConditional}
            configs={possibleLeftConfigs}
            label='Find'
            onChange={(c: Conditional) => handleChangeConditional(c)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onRemove={null}
            renderLeft={true}
            showErrors={showErrors}
          />
          {sharedPropertiesConditional && (
            <>
              <Separator />
              <ConditionalRow
                connectedBy
                conditional={sharedPropertiesConditional}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                configs={[sharedPropertiesConfigWithAllOption]}
                label='Where Accounts connected by'
                onChange={setSharedPropertiesConditional}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onRemove={null}
                renderLeft={false}
                showErrors={showErrors}
              />
            </>
          )}
          <NumHops
            numHops={numHops}
            onChange={(nums: number) => {
              setNumHops(nums);
            }}
          />
          <MinClusterSize
            minClusterSize={minClusterSize}
            onChange={(size) => {
              setMinClusterSize(size);
            }}
          />
        </Well>
      )}
    </>
  );
};

export default ByClusterQueryBody;
